/* eslint-disable complexity */
import { useAppForm } from '@/components/form';
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { UpdateFormValidationSchema } from '@/domain/termine/features/UpdateTermin/shared/UpdateFormValidationSchema';
import { dateMapper } from '@/domain/termine/features/UpdateTermin/shared/utils/formMappers';
import { useUpdateTermin } from '@/domain/termine/hooks/useUpdateTermin';
import { TerminWiederholung } from '@/domain/termine/shared/types';
import { Termin } from '@/domain/termine/shared/types/Termin';
import { UpdateFormValues } from '@/domain/termine/shared/types/UpdateTermineInput';
import { fileToBase64 } from '@/domain/termine/shared/utils/imageProcessing';
import { useProfile } from '@/hooks';
import { useToast } from '@/hooks/use-toast';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

/* eslint-disable max-lines-per-function */
export function useUpdateTerminForm(termin?: Termin) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { manager } = useProfile();
  const { updateTermin } = useUpdateTermin();
  const { toast } = useToast();
  const { standortId } = useCalendarContext();

  function formValuesIntoTerminUpdate(
    data: UpdateFormValues,
  ): UpdateFormValues {
    const mapped = dateMapper(data);
    return {
      id: data.id,
      titel: data.titel,
      beschreibung: data.beschreibung || '',
      datum: mapped.dateOnly,
      beginn: mapped.beginnDateTime || '',
      ende: mapped.endeDateTime || '',
      specials: data.specials || '',
      markierung: data.markierung,
      wiederholung: data.wiederholung || TerminWiederholung.OHNE,
      wiederholungBis: mapped.wiederholungBisTime ?? '',
      terminBild: data.terminBild,
      terminKalenderId: data.terminKalenderId,
    };
  }

  return useAppForm({
    defaultValues: {
      id: termin?.id ?? '',
      titel: termin?.titel ?? '',
      beschreibung: termin?.beschreibung ?? '',
      datum: termin?.datum ?? '',
      beginn: termin?.beginn ? dayjs(termin.beginn).format('HH:mm') : undefined,
      ende: termin?.ende ? dayjs(termin.ende).format('HH:mm') : undefined,
      markierung: termin?.markierung ?? 'GRAY',
      specials: termin?.specials,
      wiederholung: termin?.wiederholung ?? TerminWiederholung.OHNE,
      wiederholungBis: termin?.wiederholungBis ?? '',
      terminBild: termin?.image
        ? {
            contentType: termin.image.contentType ?? '',
            url: termin.image.url ?? '',
            thumbnailUrl: termin.image.thumbnailUrl ?? '',
            name: termin.image?.name ?? '',
            file: termin.image.file,
          }
        : undefined,
      terminKalenderId: termin?.idTerminkalender ?? 'NO_KALENDER',
    } satisfies z.infer<
      ReturnType<typeof UpdateFormValidationSchema>
    > as z.infer<ReturnType<typeof UpdateFormValidationSchema>>,
    validators: {
      onBlur: UpdateFormValidationSchema(),
    },
    onSubmit: async ({ value }) => {
      if (!termin) return;
      const terminBild = value.terminBild;
      let terminBildInput:
        | { name: string; base64Image: string; idStandort: string }
        | undefined;
      if (terminBild?.file) {
        if (!standortId) throw new Error('standortId ist pflicht');
        const base64Data = await fileToBase64(terminBild.file);
        terminBildInput = {
          name: terminBild.name || terminBild.file.name,
          base64Image: base64Data,
          idStandort: standortId,
        };
      }

      const terminInput = {
        ...formValuesIntoTerminUpdate(value),
        terminBildInput,
      };

      if (!manager) {
        throw new Error('Manager not found');
      }

      const response = await updateTermin({
        managerId: manager.id,
        termin: terminInput,
      });

      if (!response) {
        throw Error('No response from updateTermin mutation');
      }

      if (response.error) {
        return toast({
          title: t('domain.termine.form.messages.terminBearbeiten.error.titel'),
          description:
            response.error.message ||
            t(
              'domain.termine.form.messages.terminBearbeiten.error.description',
            ),
          variant: 'destructive',
        });
      }

      toast({
        title: t('domain.termine.form.messages.terminBearbeiten.titel'),
      });

      const terminDate = dayjs(value.datum);
      const terminYear = terminDate.format('YYYY');
      const terminMonth = terminDate.format('M');
      const terminDay = terminDate.format('D');
      navigate(`/termine/uebersicht/${terminYear}/${terminMonth}/${terminDay}`);
    },
  });
}
